import React from 'react';
import { PropTypes } from '../../../utils/propTypes';
import { ICON_REGISTRY, DEFAULT_ICON } from './registry';
import styles from './Icon.module.css';

/**
 * Icon — the single way to render a product icon.
 *
 *   <Icon name="settings" size="sm" />
 *   <Icon name="project" size="md" />
 *   <Icon name="warning" tone="danger" />
 *   <Icon name="delete" label="Delete project" />   // semantic (a11y) icon
 *
 * Looks the `name` up in the centralized registry (registry.js) — the only
 * module allowed to import from lucide-react. Standardizes size, stroke width,
 * tone color, and accessibility so icons stay visually consistent and easy to
 * refactor in one place.
 *
 * Accessibility:
 *  - Decorative by default: aria-hidden + focusable="false" (the icon adds no
 *    meaning a screen reader needs — the adjacent text carries it).
 *  - Pass `label` to make it a meaningful image: role="img" + aria-label, and
 *    the aria-hidden is dropped.
 */

/** Semantic size → px. `size` also accepts a raw number for one-offs. */
const SIZE_MAP = { xs: 14, sm: 16, md: 20, lg: 24, xl: 32 };

/**
 * @typedef {Object} IconProps
 * @property {string} [name] Semantic registry name (kebab-case).
 * @property {number | 'xs' | 'sm' | 'md' | 'lg' | 'xl'} [size] Size keyword or raw px number.
 * @property {'default' | 'primary' | 'danger' | 'success' | 'warning' | 'muted' | 'neutral'} [tone]
 * @property {number} [strokeWidth]
 * @property {string} [label] Pass to make the icon a meaningful image (role="img").
 * @property {string} [className]
 */

const TONE_CLASS = {
  default: '',
  primary: styles.tonePrimary,
  danger: styles.toneDanger,
  success: styles.toneSuccess,
  warning: styles.toneWarning,
  muted: styles.toneMuted,
  neutral: styles.toneNeutral,
};

/** @param {IconProps} props */
export default function Icon({
  name,
  size = 'md',
  tone = 'default',
  strokeWidth = 2,
  label,
  className = '',
  ...rest
}) {
  const Glyph = ICON_REGISTRY[name] || DEFAULT_ICON;

  if (!ICON_REGISTRY[name] && import.meta.env && import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(
      `[Icon] Unknown icon name "${name}". Add it to src/components/ui/Icon/registry.js. Falling back to "help".`
    );
  }

  const px = typeof size === 'number' ? size : SIZE_MAP[size] ?? SIZE_MAP.md;
  const toneClass = TONE_CLASS[tone] ?? '';
  const a11y = label
    ? { role: 'img', 'aria-label': label }
    : { 'aria-hidden': true, focusable: 'false' };

  return (
    <Glyph
      size={px}
      strokeWidth={strokeWidth}
      className={`${styles.icon} ${toneClass} ${className}`.trim()}
      {...a11y}
      {...rest}
    />
  );
}

Icon.propTypes = {
  name: PropTypes.string,
  // size / strokeWidth accept string|number — validated at the call site.
  tone: PropTypes.string,
  label: PropTypes.string,
  className: PropTypes.string,
};
